import { t } from "@lingui/macro";
import { AnalyzeResumeResult } from "@reactive-resume/schema";
import { useTheme } from "@reactive-resume/hooks";
import { cn } from "@reactive-resume/utils";
import {
  CheckCircle2,
  Info,
  AlertTriangle,
  ArrowRight,
  Clock,
  TrendingUp,
  Target,
  Zap,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useState } from "react";

type RecencyWeightingProps = {
  analysesData: AnalyzeResumeResult;
  className?: string;
};

export const RecencyWeighting = ({ analysesData, className }: RecencyWeightingProps) => {
  const { isDarkMode } = useTheme();
  const [showAllRecent, setShowAllRecent] = useState(false);
  const [showAllMissing, setShowAllMissing] = useState(false);

  // Initial number of items to show
  const INITIAL_RECENT_TO_SHOW = 6;
  const INITIAL_MISSING_TO_SHOW = 6;

  // Return null if data doesn't exist
  if (!analysesData.metrics.rw || !analysesData.metrics.rw.object) {
    return null;
  }

  const { job_skills, resume_skill_evidence } = analysesData.metrics.rw.object;

  // Get overall score - same calculation as other metric components
  const score =
    analysesData.metrics.rw.weight > 0
      ? analysesData.metrics.rw.weightedScore / analysesData.metrics.rw.weight
      : 0;

  // Define colors - unified with other metric components
  const colors = {
    green: isDarkMode ? "#66bb6a" : "#81c784",
    red: isDarkMode ? "#f44336" : "#d32f2f",
    amber: isDarkMode ? "#ff6f00" : "#e65100",
    blue: "#4dabf5",
    greenBg: isDarkMode ? "rgba(102, 187, 106, 0.15)" : "rgba(129, 199, 132, 0.1)",
    redBg: isDarkMode ? "rgba(244, 67, 54, 0.15)" : "rgba(211, 47, 47, 0.1)",
    amberBg: isDarkMode ? "rgba(255, 111, 0, 0.15)" : "rgba(230, 81, 0, 0.1)",
    blueBg: isDarkMode ? "rgba(77, 171, 245, 0.15)" : "rgba(77, 171, 245, 0.1)",
  };

  // Determine status based on score
  const recencyPercentage = Math.round(score * 100);
  let status: "excellent" | "good" | "needs_work" = "needs_work";
  if (recencyPercentage >= 80) status = "excellent";
  else if (recencyPercentage >= 60) status = "good";

  // Get status information
  const getStatusInfo = () => {
    switch (status) {
      case "excellent":
        return {
          text: t`Excellent`,
          description: t`Your skills are recent and well-aligned with current job requirements.`,
          action: t`Your recent experience is a strong selling point - highlight this!`,
          bgColor: colors.greenBg,
          textColor: colors.green,
        };
      case "good":
        return {
          text: t`Good`,
          description: t`Most of your skills are reasonably recent, but some areas could be updated.`,
          action: t`Focus on refreshing older skills and emphasizing recent experience.`,
          bgColor: colors.blueBg,
          textColor: colors.blue,
        };
      default:
        return {
          text: t`Needs Work`,
          description: t`Many required skills appear outdated or missing from recent experience.`,
          action: t`Consider updating skills or highlighting more recent relevant experience.`,
          bgColor: colors.redBg,
          textColor: colors.red,
        };
    }
  };

  const statusInfo = getStatusInfo();

  // Calculate current year for age calculations
  const currentYear = new Date().getFullYear();

  // Process skills evidence with recency scores
  const skillsWithRecency = resume_skill_evidence.map((evidence) => {
    const age = currentYear - evidence.year;
    const recencyScore = age <= 2 ? "recent" : age <= 5 ? "moderate" : "outdated";
    return {
      ...evidence,
      age,
      recencyScore,
    };
  });

  // Sort by year (most recent first)
  skillsWithRecency.sort((a, b) => b.year - a.year);

  // Get recent skills (within last 3 years)
  const recentSkills = skillsWithRecency.filter((skill) => skill.age <= 3);

  // Get missing skills (in job but not in resume evidence)
  const missingSkills = job_skills.filter(
    (skill) => !resume_skill_evidence.some((evidence) => evidence.skill === skill),
  );

  // Format skill display
  const formatSkill = (skill: string) => {
    return skill;
  };

  // Format age display
  const formatAge = (age: number) => {
    if (age === 0) return t`Current`;
    if (age === 1) return t`1 year ago`;
    return t`${age} years ago`;
  };

  // Get badge styles based on recency
  const getRecencyBadgeStyles = (recencyScore: string) => {
    switch (recencyScore) {
      case "recent":
        return { bgColor: colors.greenBg, textColor: colors.green };
      case "moderate":
        return { bgColor: colors.amberBg, textColor: colors.amber };
      default:
        return { bgColor: colors.redBg, textColor: colors.red };
    }
  };

  // Get evidence strength color
  const getStrengthColor = (strength: string) => {
    switch (strength) {
      case "high":
        return colors.green;
      case "medium":
        return colors.amber;
      default:
        return colors.red;
    }
  };

  // Prepare data for display
  const recentToShow = showAllRecent ? recentSkills : recentSkills.slice(0, INITIAL_RECENT_TO_SHOW);
  const missingToShow = showAllMissing
    ? missingSkills
    : missingSkills.slice(0, INITIAL_MISSING_TO_SHOW);

  return (
    <div className={cn("grid gap-y-6", className)}>
      <div className="grid gap-y-5">
        {/* Status Overview */}
        <div
          className="rounded-lg p-4 transition-colors"
          style={{
            backgroundColor: statusInfo.bgColor,
            borderLeft: `4px solid ${statusInfo.textColor}`,
          }}
        >
          <div className="mb-2 flex items-center gap-x-2">
            <Info className="h-5 w-5" style={{ color: statusInfo.textColor }} />
            <span className="text-lg font-semibold" style={{ color: statusInfo.textColor }}>
              {statusInfo.text} ({recencyPercentage}%)
            </span>
          </div>
          <p className="mb-2 text-sm text-slate-700 dark:text-slate-300">
            {statusInfo.description}
          </p>
          <div
            className="flex items-center gap-x-2 text-sm font-medium"
            style={{ color: statusInfo.textColor }}
          >
            <ArrowRight className="h-4 w-4" />
            <span>{statusInfo.action}</span>
          </div>
        </div>

        {/* Skills to Highlight */}
        {missingSkills.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <Target className="h-5 w-5 text-amber-600" />
                {t`Skills to Highlight`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`Required skills that aren't clearly shown in your recent experience.`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {missingToShow.map((skill, index) => (
                <div
                  key={index}
                  className="flex items-center gap-x-2 rounded-full px-3 py-1 text-sm"
                  style={{ backgroundColor: colors.redBg, color: colors.red }}
                >
                  <span>{formatSkill(skill)}</span>
                </div>
              ))}
            </div>

            {missingSkills.length > INITIAL_MISSING_TO_SHOW && (
              <button
                onClick={() => setShowAllMissing(!showAllMissing)}
                className="mt-3 flex w-full items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                {showAllMissing ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    {t`Show Less`}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    {t`Show ${missingSkills.length - INITIAL_MISSING_TO_SHOW} More`}
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Your Recent Skills */}
        {recentSkills.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <Zap className="h-5 w-5 text-green-600" />
                {t`Your Recent Skills`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`Skills you've used recently that match job requirements.`}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {recentToShow.map((skill, index) => {
                const badgeStyles = getRecencyBadgeStyles(skill.recencyScore);
                return (
                  <div
                    key={index}
                    className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50"
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-sm font-medium">{formatSkill(skill.skill)}</span>
                      <div className="flex items-center gap-x-2">
                        <span
                          className="rounded-full px-2 py-1 text-xs"
                          style={{
                            backgroundColor: badgeStyles.bgColor,
                            color: badgeStyles.textColor,
                          }}
                        >
                          {formatAge(skill.age)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      {skill.evidence_context}
                    </p>
                  </div>
                );
              })}
            </div>

            {recentSkills.length > INITIAL_RECENT_TO_SHOW && (
              <button
                onClick={() => setShowAllRecent(!showAllRecent)}
                className="mt-3 flex w-full items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                {showAllRecent ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    {t`Show Less`}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    {t`Show ${recentSkills.length - INITIAL_RECENT_TO_SHOW} More`}
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
